import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import {
  FileText,
  Search,
  RotateCcw,
  BookOpen,
  User,
  Grid,
  List,
  Eye,
  Download,
} from 'lucide-react';
import JubileeDocumentViewerModal from '../viewers/JubileeDocumentViewerModal';
import { jubileeStorageService } from '../services/jubileeStorageService';

export default function JubileeDocumentArchive({ initialFilter = {} }) {
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [viewLayout, setViewLayout] = useState('grid'); // 'grid' | 'list'

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState(initialFilter.year || 'all');
  const [selectedEvent, setSelectedEvent] = useState(initialFilter.eventId || 'all');
  const [selectedCategory, setSelectedCategory] = useState(initialFilter.categoryId || 'all');

  const allDocs = useMemo(() => jubileeStorageService.getMediaByType('document'), []);
  const events = useMemo(() => jubileeStorageService.getEvents(), []);
  const categories = useMemo(() => jubileeStorageService.getCategories(), []);

  const filteredDocs = useMemo(() => {
    return jubileeStorageService.searchMedia({
      mediaType: 'document',
      query: searchQuery,
      year: selectedYear,
      eventId: selectedEvent,
      categoryId: selectedCategory,
    });
  }, [searchQuery, selectedYear, selectedEvent, selectedCategory]);

  const handleOpenDoc = (doc) => {
    setSelectedDoc(doc);
    setShowDocViewer(true);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedYear('all');
    setSelectedEvent('all');
    setSelectedCategory('all');
  };

  const hasActiveFilters = searchQuery !== '' || selectedYear !== 'all' || selectedEvent !== 'all' || selectedCategory !== 'all';
  const availableYears = Array.from(new Set(allDocs.map((d) => d.year))).sort((a, b) => b - a);

  return (
    <div className="py-4">
      <Container>
        {/* Archive Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4 pb-3 border-bottom">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <div className="p-2 rounded-3 text-white d-inline-flex" style={{ background: '#059669' }}>
                <FileText size={22} />
              </div>
              <h2 className="fw-bold m-0 text-slate-900" style={{ letterSpacing: '-0.5px' }}>
                Document Archive & Heritage Records
              </h2>
            </div>
            <p className="text-muted small m-0">
              Read Jubilee souvenir volumes, Roman foundation decrees, research monographs, and Shembaganur mission diaries online.
            </p>
          </div>

          <div className="d-flex align-items-center gap-2">
            {/* View Mode Toggle */}
            <div className="btn-group btn-group-sm">
              <button
                className={`btn ${viewLayout === 'grid' ? 'btn-success' : 'btn-outline-secondary'}`}
                onClick={() => setViewLayout('grid')}
                title="Grid View"
              >
                <Grid size={15} />
              </button>
              <button
                className={`btn ${viewLayout === 'list' ? 'btn-success' : 'btn-outline-secondary'}`}
                onClick={() => setViewLayout('list')}
                title="List View"
              >
                <List size={15} />
              </button>
            </div>

            <span
              className="badge px-3 py-2 rounded-pill fw-semibold"
              style={{ background: '#d1fae5', color: '#065f46', border: '1px solid #a7f3d0' }}
            >
              {filteredDocs.length} {filteredDocs.length === 1 ? 'Document' : 'Documents'} Available
            </span>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white p-3 rounded-4 shadow-sm border mb-4">
          <Row className="g-2 align-items-center">
            <Col xs={12} md={4}>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light border-end-0">
                  <Search size={15} className="text-muted" />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search documents by title, author, decree..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-start-0"
                />
              </div>
            </Col>

            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
              >
                <option value="all">All Events</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="all">All Years</option>
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    Year {y}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={2} className="text-end">
              {hasActiveFilters && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleClearFilters}
                  className="w-100 d-flex align-items-center justify-content-center gap-1"
                >
                  <RotateCcw size={13} /> Clear Filters
                </Button>
              )}
            </Col>
          </Row>
        </div>

        {/* Document Grid / List */}
        {filteredDocs.length > 0 ? (
          viewLayout === 'grid' ? (
            <Row className="g-4">
              {filteredDocs.map((doc) => {
                const isPdf = doc.mimeType?.includes('pdf') || doc.fileName?.endsWith('.pdf');
                return (
                  <Col key={doc.id} xs={12} sm={6} lg={4}>
                    <div
                      className="jubilee-media-card cursor-pointer"
                      onClick={() => handleOpenDoc(doc)}
                    >
                      <div className="media-thumbnail-wrapper" style={{ height: '180px', background: isPdf ? '#fef2f2' : '#eff6ff' }}>
                        <img
                          src={doc.thumbnailUrl}
                          alt={doc.title}
                          className="media-thumbnail-img"
                          style={{ objectFit: 'cover', opacity: 0.85 }}
                          loading="lazy"
                        />
                        <span className="media-type-badge document">
                          <FileText size={12} /> {isPdf ? 'PDF Book' : 'Document'}
                        </span>
                        {doc.pageCount && (
                          <span className="media-duration-badge" style={{ background: 'rgba(5, 150, 105, 0.9)' }}>
                            {doc.pageCount} Pages
                          </span>
                        )}
                        <div className="media-play-overlay">
                          <div className="play-circle-btn" style={{ background: '#059669', color: '#fff' }}>
                            <Eye size={22} />
                          </div>
                        </div>
                      </div>

                      <div className="media-card-body">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: '0.72rem' }}>
                            Year {doc.year}
                          </span>
                          {doc.categoryName && (
                            <span className="badge bg-light text-secondary border text-truncate" style={{ fontSize: '0.72rem', maxWidth: '140px' }}>
                              {doc.categoryName}
                            </span>
                          )}
                        </div>

                        <h5 className="media-card-title">{doc.title}</h5>
                        <p className="media-card-desc">{doc.description}</p>

                        {doc.author && (
                          <div className="small text-muted mb-2 d-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
                            <User size={13} className="text-success" />
                            <span className="text-truncate">Author: {doc.author}</span>
                          </div>
                        )}

                        <div className="media-card-meta">
                          <span className="text-truncate" style={{ maxWidth: '160px' }}>
                            {doc.eventName || 'Heritage Record'}
                          </span>
                          <span className="fw-semibold text-success small d-flex align-items-center gap-1">
                            <BookOpen size={14} /> Preview Document →
                          </span>
                        </div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          ) : (
            /* List Layout */
            <div className="d-flex flex-column gap-3">
              {filteredDocs.map((doc) => {
                const isPdf = doc.mimeType?.includes('pdf') || doc.fileName?.endsWith('.pdf');
                return (
                  <div
                    key={doc.id}
                    className="bg-white p-3 rounded-3 border shadow-sm d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 cursor-pointer hover-shadow"
                    onClick={() => handleOpenDoc(doc)}
                    style={{ transition: 'all 0.2s ease' }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-3 p-3 d-flex align-items-center justify-content-center text-white"
                        style={{ background: isPdf ? '#dc2626' : '#059669', width: '50px', height: '50px', flexShrink: 0 }}
                      >
                        <FileText size={24} />
                      </div>
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: '0.7rem' }}>
                            {doc.year}
                          </span>
                          <span className="badge bg-light text-secondary border" style={{ fontSize: '0.7rem' }}>
                            {doc.categoryName}
                          </span>
                          {doc.pageCount && (
                            <span className="text-muted small" style={{ fontSize: '0.75rem' }}>
                              • {doc.pageCount} pages
                            </span>
                          )}
                        </div>
                        <h6 className="fw-bold text-dark m-0">{doc.title}</h6>
                        <div className="text-muted small text-truncate" style={{ maxWidth: '600px' }}>
                          {doc.description}
                        </div>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2 ms-auto">
                      <Button
                        variant="success"
                        size="sm"
                        className="d-flex align-items-center gap-1 rounded-pill px-3 shadow-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDoc(doc);
                        }}
                      >
                        <Eye size={15} /> Read Online
                      </Button>
                      <a
                        href={doc.fileUrl}
                        download={doc.fileName}
                        onClick={(e) => e.stopPropagation()}
                        className="btn btn-sm btn-outline-secondary rounded-pill p-2"
                        title="Download file"
                      >
                        <Download size={15} />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          <div className="text-center py-5 bg-white rounded-4 border my-4">
            <div className="p-3 rounded-circle d-inline-flex mb-3" style={{ background: '#d1fae5', color: '#065f46' }}>
              <FileText size={36} />
            </div>
            <h5 className="fw-bold text-dark">No documents available for this filter</h5>
            <p className="text-muted small mb-3">
              Try adjusting your search criteria or clearing active filters.
            </p>
            {hasActiveFilters && (
              <Button variant="outline-success" size="sm" onClick={handleClearFilters}>
                Clear All Filters
              </Button>
            )}
          </div>
        )}
      </Container>

      {/* Google Drive Style Document Viewer Modal */}
      <JubileeDocumentViewerModal
        document={selectedDoc}
        show={showDocViewer}
        onHide={() => setShowDocViewer(false)}
      />
    </div>
  );
}
